/* global google */
import { useEffect, useRef, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: { streetAddress1: string; city: string; state: string; zip: string }) => void;
  onBlur?: () => void;
  error?: string;
}

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let loaderConfigured = false;

const AddressAutocomplete = ({ value, onChange, onAddressSelect, onBlur, error }: AddressAutocompleteProps) => {
  const onChangeRef = useRef(onChange);
  const onAddressSelectRef = useRef(onAddressSelect);
  const inputRef = useRef<HTMLInputElement>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    onChangeRef.current = onChange;
    onAddressSelectRef.current = onAddressSelect;
  }, [onChange, onAddressSelect]);

  useEffect(() => {
    let cancelled = false;
    let autocomplete: google.maps.places.Autocomplete | undefined;
    let listener: google.maps.MapsEventListener | undefined;

    const initialize = async () => {
      if (!API_KEY) {
        setLoadError('Address suggestions are not configured.');
        return;
      }

      try {
        if (!loaderConfigured) {
          setOptions({
            key: API_KEY,
            v: 'weekly',
            region: 'US'
          });
          loaderConfigured = true;
        }

        await importLibrary('places');

        if (cancelled || !inputRef.current) return;

        autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address']
        });

        listener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete?.getPlace();
          if (!place?.address_components) {
            setLoadError('Select a suggested address, or enter it manually.');
            return;
          }

          const components = place.address_components;
          const getComponent = (type: string) =>
            components.find((component) => component.types.includes(type))?.long_name ?? '';

          const streetNumber = getComponent('street_number');
          const route = getComponent('route');
          const city = getComponent('locality') || getComponent('sublocality_level_1') || getComponent('postal_town');
          const state = getComponent('administrative_area_level_1');
          const zip = getComponent('postal_code');

          onAddressSelectRef.current({
            streetAddress1: [streetNumber, route].filter(Boolean).join(' '),
            city,
            state,
            zip
          });
          setLoadError('');
        });
      } catch {
        if (!cancelled) {
          setLoadError('Address suggestions could not load. Please try again later.');
        }
      }
    };

    initialize().catch(() => {
      if (!cancelled) {
        setLoadError('Address suggestions could not load. Please try again later.');
      }
    });

    return () => {
      cancelled = true;
      listener?.remove();
    };
  }, []);

  return (
    <div>
      <label className="font-semibold text-sm tracking-[1.1375px]" htmlFor="streetAddress1">
        STREET ADDRESS 1
      </label>
      <div className="mt-2">
        <input
          ref={inputRef}
          id="streetAddress1"
          name="streetAddress1"
          type="text"
          autoComplete="street-address"
          placeholder="Start typing an address"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onBlur={onBlur}
          className={`w-full p-4 text-sm rounded-sm border ${error ? 'border-red-600' : 'border-neutral-400'}`}
          style={{
            padding: '10px 12px',
            fontSize: '0.8125rem',
            height: '40px',
            boxSizing: 'border-box'
          }}
        />
        {error && (
          <p role="alert" className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
      {loadError && (
        <p role="status" className="mt-1 text-sm text-neutral-600">
          {loadError}
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
